import {
    LegacyActivityId,
    LegacyJournalId,
    LegacySymptomId,
    type TrackableData,
    TrackableType,
} from "@qendo/trackables";
import { ucfirst } from "@samlevy/string-helpers";
import { type DateArg, isSameDay, min, toDate } from "date-fns";
import type { Href } from "expo-router";
import { useCallback, useMemo, useState } from "react";
import { View } from "react-native";
import { Button } from "~/components/Button";
import { type Column, Columns } from "~/components/layout/Columns";
import { FilteredList } from "~/components/layout/FilteredList";
import { ListingGroup } from "~/components/layout/ListingGroup";
import { ListingItem } from "~/components/layout/ListingItem";
import { FullPageModal, type FullPageModalProps } from "~/components/layout/modal/FullPageModal";
import { Can } from "~/components/permission/Can";
import { CanAny } from "~/components/permission/CanAny";
import { Gap, Rounded } from "~/constants/layout";
import { useJournal } from "~/providers/JournalProvider";
import { useProfile } from "~/providers/ProfileProvider";
import { useTheme } from "~/providers/ThemeProvider";
import { useTrackable } from "~/providers/TrackableProvider";
import { formatDateForHumans, formatDateString } from "~/util/dates";

export type AddLogProps = {
    onRequestClose: () => void;
    onDate?: DateArg<Date>;
    searchFor?: TrackableType | null;
} & Partial<FullPageModalProps>;

export const AddLog = ({ onRequestClose, onDate, searchFor, ...modalProps }: AddLogProps) => {
    const { colours } = useTheme();
    const { can, cannot } = useProfile();
    const [search, setSearch] = useState<TrackableType | null>(searchFor ?? null);
    const { listTrackables, findTrackable, refreshTrackables } = useTrackable();
    const { recentlyLogged } = useJournal();
    const flare = findTrackable(LegacySymptomId.flare) as TrackableData;
    const period = findTrackable(LegacyJournalId.period) as TrackableData;
    const water = findTrackable(LegacyJournalId.water) as TrackableData;
    const mood = findTrackable(LegacySymptomId.mood) as TrackableData;
    const sleep = findTrackable(LegacyActivityId.sleep) as TrackableData;
    const journal = findTrackable(LegacyJournalId.journal) as TrackableData;
    const food = findTrackable(LegacyJournalId.food) as TrackableData;
    const medications = findTrackable(LegacyActivityId.medications) as TrackableData;

    const now = new Date();
    const [date, setDate] = useState(min([now, toDate(onDate ?? now)]));
    const is_today = isSameDay(date, now);

    const onCancel = useCallback(() => (search ? setSearch(null) : onRequestClose()), [search, onRequestClose]);

    const items = useMemo(() => (search ? listTrackables(search) : []), [search, listTrackables]);

    const searchGroups = useMemo(
        () =>
            search
                ? [
                      {
                          name: "Recent",
                          title: `Recent ${ucfirst(search)}s`,
                          matches: (item: TrackableData) => recentlyLogged.includes(item.id),
                      },
                      { name: `All`, title: `All ${ucfirst(search)}s`, matches: () => true },
                  ]
                : [],
        [search, recentlyLogged],
    );

    const for_date = is_today ? undefined : formatDateString(date);

    const path_for = (id: string): Href => ({
        pathname: "/journal/[id]/create",
        params: { id, date: for_date },
    });

    // biome-ignore lint/correctness/useExhaustiveDependencies(flare): should never change
    // biome-ignore lint/correctness/useExhaustiveDependencies(period): should never change
    const header_items: Column[] = useMemo(
        () =>
            [
                can("update", "symptoms")
                    ? () => {
                          const { icon, title, id } = flare;

                          return (
                              <ListingItem
                                  icon={icon}
                                  title={title}
                                  href={{
                                      pathname: "/journal/[id]/create",
                                      params: { id, date: for_date },
                                  }}
                                  colour={colours.button.destructive}
                                  style={Rounded.sm}
                                  hideChevron
                                  last
                                  replace
                              />
                          );
                      }
                    : null,
                can("update", "cycle")
                    ? () => {
                          const { icon, title } = period;

                          return (
                              <ListingItem
                                  icon={icon}
                                  title={title}
                                  href={{
                                      pathname: "/periods/create",
                                      params: { date: for_date },
                                  }}
                                  style={Rounded.sm}
                                  hideChevron
                                  last
                                  replace
                              />
                          );
                      }
                    : null,
                can("update", "symptoms")
                    ? () => (
                          <ListingItem
                              title="Symptom"
                              icon="symptoms"
                              onPress={() => setSearch(TrackableType.symptom)}
                              style={Rounded.sm}
                              hideChevron
                              last
                          />
                      )
                    : null,
                can("update", "activities")
                    ? () => (
                          <ListingItem
                              title="Activity"
                              icon="movement"
                              onPress={() => setSearch(TrackableType.activity)}
                              style={Rounded.sm}
                              hideChevron
                              last
                          />
                      )
                    : null,
            ].filter((v) => Boolean(v)),
        [can, for_date, colours],
    );

    return (
        <FullPageModal
            {...modalProps}
            cancelable
            title={`Add ${search?.toLowerCase() ?? "log"} ${
                is_today ? "" : `for ${formatDateForHumans(date, "approximate", true)}`
            }`.trim()}
            onRequestClose={onCancel}
            scrollable={!search}
            headerRight={
                is_today
                    ? undefined
                    : () => <Button variant="quiet" title="Today" size="sm" onPress={() => setDate(now)} />
            }
        >
            {search ? (
                <FilteredList<TrackableData>
                    data={items}
                    renderItem={({ item }) => (
                        <ListingItem
                            title={item.title}
                            icon={item.icon}
                            href={path_for(item.id)}
                            replace
                            hideChevron
                            last
                        />
                    )}
                    searchWith={(i, s) => i.title.toLowerCase().includes(s.toLowerCase())}
                    withoutTabs
                    refreshWith={refreshTrackables}
                    groups={searchGroups}
                />
            ) : (
                <View style={Gap.lg}>
                    <Columns items={header_items} columns={2} />
                    <CanAny ability="update" section={["journal", "activities"]}>
                        <ListingGroup>
                            <Can ability="update" section="activities">
                                <ListingItem
                                    title={medications.title}
                                    icon={medications.icon}
                                    href={path_for(medications.id)}
                                    last={cannot("update", "journal")}
                                    replace
                                    hideChevron
                                />
                            </Can>
                            <Can ability="update" section="journal">
                                <ListingItem
                                    title={water.title}
                                    icon={water.icon}
                                    href={path_for(water.id)}
                                    replace
                                    hideChevron
                                />
                                <ListingItem
                                    title={mood.title}
                                    icon={mood.icon}
                                    href={path_for(mood.id)}
                                    replace
                                    hideChevron
                                />
                                <ListingItem
                                    title={sleep.title}
                                    icon={sleep.icon}
                                    href={path_for(sleep.id)}
                                    replace
                                    hideChevron
                                    last
                                />
                            </Can>
                        </ListingGroup>
                        <Can ability="update" section="journal">
                            <ListingGroup>
                                <ListingItem
                                    title={journal.title}
                                    icon={journal.icon}
                                    href={path_for(journal.id)}
                                    replace
                                    hideChevron
                                />
                                <ListingItem
                                    title={food.title}
                                    icon={food.icon}
                                    href={path_for(food.id)}
                                    replace
                                    hideChevron
                                    last
                                />
                            </ListingGroup>
                        </Can>
                    </CanAny>
                    <Can ability="update" section="calendar">
                        <ListingGroup>
                            <ListingItem
                                title="Calendar event"
                                icon="calendar"
                                href="/calendar/add"
                                replace
                                hideChevron
                                last
                            />
                        </ListingGroup>
                    </Can>
                </View>
            )}
        </FullPageModal>
    );
};
